import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, map } from 'rxjs';
import { ApiService } from '../../../SGRE-shared/services/api.service';
import { CartService } from '../../cart/services/cart.service';
import { CheckoutAccordion, CheckoutForm, CheckoutSummary, CheckoutTable, DeliveryAddress, Entry, Product } from '../models/checkoutDetails';

@Injectable({
  providedIn: 'root',
})
export class SharedCheckoutService {

  private addressSubject = new BehaviorSubject<any>(null);
  addresses = this.addressSubject.asObservable();

  private showModalSubject = new Subject<void>();
  showModal$ = this.showModalSubject.asObservable();

  private orderBooleanService = new BehaviorSubject<boolean>(false);
  public showPlaceOrder = this.orderBooleanService.asObservable();

  private errorBooleanService = new BehaviorSubject<boolean>(false);
  public showSimulationError = this.errorBooleanService.asObservable();

  private quotationData = new BehaviorSubject<boolean>(false);
  public isQuatationData = this.quotationData.asObservable();

  private nonQuotationDataLength = new BehaviorSubject<boolean>(false);
  public isNonQuatationData = this.nonQuotationDataLength.asObservable();

  private errorStringSource = new BehaviorSubject<string>('');
  public simulationErrorMessage = this.errorStringSource.asObservable();

  private cartGroupDataSource = new BehaviorSubject<any>(null);
  public cartGroupData$ = this.cartGroupDataSource.asObservable();

  private isBtnClickSubject = new BehaviorSubject<boolean>(false);
  public isBtnClick$ = this.isBtnClickSubject.asObservable();

  private currentTotalSubject = new BehaviorSubject<number>(0);
  currentTotal$ = this.currentTotalSubject.asObservable();

  private minimumOrderAmountSubject = new BehaviorSubject<number>(0);
  minimumOrderAmount$ = this.minimumOrderAmountSubject.asObservable();

  /** Checkout Code Rewrite Implementation below */
  private updateCartSubject = new BehaviorSubject<CheckoutAccordion>(null);
  public updateCart$ = this.updateCartSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private cartService: CartService
  ) { }

  getAccordionData(cartId: string): Observable<any> {
    let paramsObject = { fields: 'FULL' };
    return this.apiService.getCartsList(cartId, paramsObject);
  }

  // getTableData(data): any[] {
  //   return data?.entries?.map((item) => this.cartService.mapData(item));
  // }

  saveCartGroupData(data: any) {
    this.cartGroupDataSource.next(data)
  }

  changeErrorMessage(data: string) {
    this.errorStringSource.next(data);
  }

  changeShowError(data: boolean) {
    this.errorBooleanService.next(data);
  }

  changePlaceOrder(data: boolean) {
    this.orderBooleanService.next(data);
  }

  triggerShowModal() {
    this.showModalSubject.next();
  }

  setClickBtn(isBtnClicked: boolean): void {
    this.isBtnClickSubject.next(isBtnClicked);
  }

  checkQuotationLength(data: boolean) {
    this.quotationData.next(data);
  }

  checkNonQuatationLength(data: boolean) {
    this.nonQuotationDataLength.next(data);
  }

  setCurrentTotal(total: number) {
    this.currentTotalSubject.next(total);
  }

  setMinimumOrderAmount(amount: number) {
    this.minimumOrderAmountSubject.next(amount);
  }

  updateShippingAddress(cartId: string, addressId: string): Observable<any> {
    return this.apiService.putShippingAddress(cartId, addressId);
  }

  clearCheckoutData() {
    this.addressSubject.next(null);
    this.showModalSubject.next(null);
    this.orderBooleanService.next(null);
    this.errorBooleanService.next(null);
    this.quotationData.next(null);
    this.nonQuotationDataLength.next(null);
    this.errorStringSource.next(null);
    this.cartGroupDataSource.next(null);
    this.isBtnClickSubject.next(null);
    this.currentTotalSubject.next(null);
    this.minimumOrderAmountSubject.next(null);
    this.apiService.addressSubject.next(null);
    this.apiService.deliveryModeSubject.next(null);
    this.apiService.addressChangeQuotationSubject.next(null);
    this.apiService.deliveryModeChangeQuotationSubject.next(null);
    this.apiService.originalCartIdSubject.next(null);
  }


  /** Checkout Code Rewrite Implementation below */
  getCheckoutCartDetails(cartId: string, orderType?: string): Observable<CheckoutAccordion> {
    let paramsObject = { fields: 'FULL' };
    return this.apiService.getCartsList(cartId, paramsObject)
      .pipe(
        map(data => this.mapCartDetails(data, orderType))
      );
  }

  mapCartDetails(data: any, orderType?: string): CheckoutAccordion {
    let cartsData: CheckoutAccordion;

    let addrObj: DeliveryAddress = null;
    if (data?.deliveryAddress) {
      addrObj = {
        companyName: (data.deliveryAddress.id) ? data.deliveryAddress.id : null,
        line1: (data.deliveryAddress.line1) ? data.deliveryAddress.line1 : null,
        town: (data.deliveryAddress.town) ? data.deliveryAddress.town : null,
        postalCode: (data.deliveryAddress.postalCode) ? data.deliveryAddress.postalCode : null
      };
    }

    let accordionForm: CheckoutForm = {
      incoterm: (data?.incoterm) ? data.incoterm : null,
      isIncotermDisabled: false,
      deliveryAddress: addrObj,
      isShippingAddressDisabled: false,
      deliveryMode: (data?.deliveryMode) ? data.deliveryMode.name : null,
      isShippingMethodDisabled: false
    };

    let accordionTable: CheckoutTable = {
      entries: data?.entries?.map((item) => this.fetchTableData(item))
    };

    let accordionSummary: CheckoutSummary = {
      currency: (data?.totalPrice?.currencyIso) ? data.totalPrice.currencyIso : null,
      subTotal: (data?.subTotal?.value) ? data.subTotal.value : 0,
      transportFee: (data?.transportFee?.value) ? data.transportFee.value : 0,
      shippingFee: (data?.shippingFee?.value) ? data.shippingFee.value : 0,
      totalTax: (data?.totalTax?.value) ? data.totalTax.value : 0,
      totalPriceWithTax: (data?.totalPriceWithTax?.value) ? data.totalPriceWithTax.value : 0,
    };

    let isQuotationFlag = (accordionTable?.entries?.length > 0 && accordionTable.entries.some(x => x.isQuotation))

    cartsData = {
      orderType: orderType ? orderType : null,
      header: isQuotationFlag ? 'checkOut.quotation' : 'checkOut.Warehouse',
      type: isQuotationFlag ? 'quotation' : 'warehouse',
      cartId: data.code,
      formData: accordionForm,
      tableData: accordionTable,
      summaryData: accordionSummary
    };

    return cartsData;
  }

  fetchTableData(entry): Entry {
    let entryObj: Entry;
    let prdctObj: Product;

    prdctObj = {
      baseProduct: entry?.product?.baseProduct,
      name: (entry?.product?.name) ? entry.product.name : '-',
      variant: entry?.product?.code.charAt(entry.product.code?.length - 1),
      leadTime: !entry?.isQuotation
        ? ((entry?.product?.stock?.estimatedLeadTime)
          ? entry.product.stock.estimatedLeadTime + ' Days'
          : null)
        : '-',
      minQuantity: (entry?.product?.minQuantity > 0)
        ? entry.product.minQuantity
        : ((entry?.product?.interval > 0) ? entry.product.interval : 1),
      interval: (entry?.product?.interval) ? entry.product.interval : 0,
      step: (entry?.product?.interval > 0) ? entry.product.interval : 1,
      navigateUrl: entry?.product?.url
    };

    entryObj = {
      product: prdctObj,
      eta: entry?.eta
        ? new Date(entry.eta).toISOString().split('T')[0]
        : '-',
      currency: (!entry?.isQuotation) ? entry?.totalPrice?.currencyIso : '-',
      basePrice: (!entry?.isQuotation) ? entry?.basePrice?.value : '-',
      quantity: (entry?.quantity) ? entry.quantity : 0,
      totalPrice: (!entry?.isQuotation) ? entry?.totalPrice?.value : '-',
      isQuotation: (entry?.isQuotation) ? entry.isQuotation : false,
      statusRequester: (entry?.isQuotation) ? entry.isQuotation : false,
      statusPurchaser: false,
      status: 'edit',
    };

    return entryObj;
  }

  getIncoterms(): Observable<any> {
    return this.apiService.getIncoterms();
  }

  getCountries(): Observable<any> {
    return this.apiService.getCountries();
  }

  getShippingAddress(cartId: string): Observable<any> {
    return this.apiService.getShippingAddress(cartId);
  }

  getShippingMethod(cartId: string): Observable<any> {
    return this.apiService.getDeliveryMethod(cartId);
  }

  emitUpdateCartSubject(data: CheckoutAccordion) {
    this.updateCartSubject.next(data);
  }
}
